import type { AxiosInstance, AxiosRequestConfig } from 'axios'
import { merge } from 'lodash-es'
import type { DeepPartial } from 'ts-essentials'

import type {
  ActivityGroupActivity,
  ActivityGroupActivityId,
  CascadeActivity,
  WithId,
} from '@/types'

import type { Options } from './BaseService.types'
import BaseService from './BaseService'

/**
 * @private
 */
export default class ActivityGroupActivityService extends BaseService<ActivityGroupActivity> {
  constructor(client: AxiosInstance) {
    super(client, 'activity-group-activity')
  }

  get<R extends DeepPartial<ActivityGroupActivity> = ActivityGroupActivity>(
    id: ActivityGroupActivity['id'],
    config?: AxiosRequestConfig & Options
  ): Promise<R> {
    let merged = config ?? {}
    if (config?.relations === true) {
      merged = merge({}, merged, {
        params: {
          join: [
            ActivityGroupActivityService.ACTIVITY_RELATION,
            ActivityGroupActivityService.ACTIVITY_MEDIA_RELATION,
            ActivityGroupActivityService.ACTIVITY_MEDIA_MEDIA_RELATION,
            ActivityGroupActivityService.ACTIVITY_STEPS_RELATION,
            ActivityGroupActivityService.ACTIVITY_STEPS_MEDIA_RELATION,
            ActivityGroupActivityService.ACTIVITY_STEPS_MEDIA_MEDIA_RELATION,
          ],
        },
      })
    }
    return super.get(id, merged)
  }

  async updateActivity(
    id: ActivityGroupActivityId,
    activity: WithId<CascadeActivity>
  ): Promise<ActivityGroupActivity> {
    const { data } = await this.client.patch<ActivityGroupActivity>(
      `/activity-group-activity/${id}/activity`,
      {
        activity,
      }
    )
    return data
  }

  static ACTIVITY_RELATION = 'activity'
  static ACTIVITY_MEDIA_RELATION = 'activity.media'
  static ACTIVITY_MEDIA_MEDIA_RELATION = 'activity.media.media'
  static ACTIVITY_STEPS_RELATION = 'activity.steps'
  static ACTIVITY_STEPS_MEDIA_RELATION = 'activity.steps.media'
  static ACTIVITY_STEPS_MEDIA_MEDIA_RELATION = 'activity.steps.media.media'
}
